import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { authUtil } from 'src/utils';
import { useEffect, useRef } from 'react';

type PublishabilityReportProps = {
  bookId: string;
  contentId: string;
};

// Statuses that mean the analyzer is working on this manuscript right now.
const PROCESSING_STATUSES = [
  'pending',
  'uploaded',
  'extracting_metadata',
  'analyzing_content',
  'analyzing_publishability',
];

// Proof that this manuscript is actually being worked on. `pending` is
// deliberately absent: the status endpoint answers `pending` for any version
// that has never been analysed, so it would swallow a genuine failed start.
const RUNNING_EVIDENCE_STATUSES = [
  'uploaded',
  'extracting_metadata',
  'analyzing_content',
  'analyzing_publishability',
];

const readStatus = (data: any) => data?.results?.status || data?.status;

// The start request can fail (or time out) while the run is actually under
// way — the API talks to an analyzer that holds the connection open for the
// whole analysis. Before blaming the start, ask the server what state the run
// is in: a run that is processing, or one that finished after we clicked, is
// not a failed start.
const isRunUnderway = (data: any, clickedAt: number) => {
  const status = readStatus(data);
  if (RUNNING_EVIDENCE_STATUSES.includes(status)) {
    return true;
  }
  if (status !== 'completed') {
    return false;
  }
  const finishedAt =
    data?.results?.completedAt || data?.results?.updatedAt || data?.updatedAt;
  return !!finishedAt && new Date(finishedAt).getTime() >= clickedAt;
};

const useBookPublishabilityReport = ({
  bookId,
  contentId,
}: PublishabilityReportProps) => {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const generatePublishabilityReport = useMutation({
    mutationFn: async () => {
      return fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}/content/${contentId}/analyze`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
        }
      );
    }
  });

  const { isPending: isLoading, mutateAsync } = generatePublishabilityReport;

  const mostRecentPublishabilityReport = useQuery<any>({
    queryKey: ['mostRecentPublishabilityReport', bookId, contentId],

    queryFn: async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}/content/${contentId}/analysis`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch manuscript analysis results');
      }
      return response.json();
    },

    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,

    // 5 seconds (shorter for polling)
    staleTime: 1000 * 5,

    // 10 minutes
    gcTime: 1000 * 60 * 10,

    retry: 1,

    // Only run if bookId and contentId are provided
    enabled: !!bookId && !!contentId
  });

  useEffect(() => {
    if (!mostRecentPublishabilityReport.isError) return;
    toast.error('Failed to fetch manuscript analysis results');
    console.error(
      'Error fetching manuscript analysis results:',
      mostRecentPublishabilityReport.error
    );
  }, [mostRecentPublishabilityReport.isError, mostRecentPublishabilityReport.error]);

  // Polling logic
  useEffect(() => {
    const data = mostRecentPublishabilityReport.data;
    const status = data?.results?.status || data?.status;

    // Start polling if status indicates processing
    const shouldPoll = status && PROCESSING_STATUSES.includes(status);

    if (shouldPoll) {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Start polling every 5 seconds
      pollingIntervalRef.current = setInterval(() => {
        mostRecentPublishabilityReport.refetch();
      }, 5000);
    } else {
      // Stop polling if status is completed, error, or not started
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    mostRecentPublishabilityReport.data?.results?.status,
    mostRecentPublishabilityReport.data?.status,
    mostRecentPublishabilityReport.refetch
  ]);

  // todo: add backend support for publishability report generation

  return {
    isLoading,
    generatePublishabilityReport: async ({
      bookId,
      contentId,
    }: PublishabilityReportProps) => {
      const clickedAt = Date.now();

      // Only report a failed start once we've checked the run isn't running
      // anyway; a genuine failure still surfaces.
      const reportStartFailure = async (message: string) => {
        try {
          const { data } = await mostRecentPublishabilityReport.refetch();
          if (isRunUnderway(data, clickedAt)) {
            console.warn(
              'Analysis start request failed but the run is under way — not showing an error'
            );
            return;
          }
        } catch (refetchError) {
          console.log('Could not verify analysis state:', refetchError);
        }
        toast.error(message);
      };

      try {
        const response = await mutateAsync();
        const body = await response.json().catch(() => ({}));
        if (!response.ok) {
          // A 4xx is the server refusing to start (no credits, no access, no
          // content) — unambiguous, so say so. Only 5xx/timeouts can mean
          // "the request broke while the analyzer was already working".
          if (response.status >= 400 && response.status < 500) {
            toast.error('Failed to start manuscript analysis');
          } else {
            await reportStartFailure('Failed to start manuscript analysis');
          }
          return body;
        }
        // Pick the run up straight away so the progress state renders.
        mostRecentPublishabilityReport.refetch();
        return body;
      } catch (error) {
        await reportStartFailure('An error occurred while starting the analysis');
        console.log('Error details:', error);
      }
    },
    mostRecentPublishabilityReport: mostRecentPublishabilityReport,
  };
};

export default useBookPublishabilityReport;
